// react
import React from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
// DateRangeWithGenerate.jsx
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';

// material ui
import { Box, Button, Popover, Tooltip } from '@mui/material';
import { StaticDateRangePicker } from '@mui/x-date-pickers-pro/StaticDateRangePicker';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { daDK } from '@mui/x-date-pickers/locales';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import { DateTime } from 'luxon';

/** Small date label helper (Danish) */
const formatDate = (date) => {
  if (!date) return '';
  const d = date.setLocale('da');
  return `${d.toFormat('d.')} ${d.toFormat('MMMM')} ${d.toFormat('yyyy')}`;
};

/** Build a few handy shortcuts */
function buildYearShortcuts(count = 4) {
  const now = DateTime.now();
  return Array.from({ length: count }, (_, i) => {
    const start = now.minus({ years: i }).startOf('year');
    const end = i === 0 ? now : start.endOf('year');
    return {
      label: i === 0 ? 'Dette år' : String(start.year),
      getValue: () => [start, end],
    };
  });
}
const defaultShortcuts = [
  ...buildYearShortcuts(2),
  {
    label: 'Sidste 7 dage',
    getValue: () => [
      DateTime.now().minus({ days: 7 }).startOf('day'),
      DateTime.now(),
    ],
  },
  {
    label: 'Sidste 4 uger',
    getValue: () => [
      DateTime.now().minus({ weeks: 4 }).startOf('day'),
      DateTime.now(),
    ],
  },
];

export default function DateRangeWithGenerate({
  startDt,
  endDt,
  onChangeRange,
  onGenerate,
  canGenerate,
  disabled = false,
  zone = 'Europe/Copenhagen',
  locale = 'da',
  generateLabel = 'Generer graf',
  shortcuts = defaultShortcuts,
}) {
  const { t } = useTranslation('energyxplorer-initiatives-tab');
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [tempRange, setTempRange] = React.useState([startDt, endDt]);

  const open = Boolean(anchorEl);
  const now = React.useMemo(() => DateTime.now().setZone(zone), [zone]);

  const handleOpen = (e) => {
    setTempRange([startDt, endDt]);
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const handleConfirmAndGenerate = () => {
    const [s, e] = tempRange || [];
    if (!s || !e) return;

    const selectedStart = s.setZone(zone).startOf('day');
    let selectedEnd = e.setZone(zone).endOf('day');
    if (selectedEnd > now) selectedEnd = now;

    if (
      selectedStart <= now &&
      selectedEnd <= now &&
      selectedEnd > selectedStart
    ) {
      onChangeRange?.(selectedStart, selectedEnd);
      onGenerate?.(selectedStart.toMillis(), selectedEnd.toMillis());
    }
    handleClose();
  };

  const triggerLabel =
    startDt && endDt
      ? `${formatDate(startDt.setZone(zone))} - ${formatDate(endDt.setZone(zone))}`
      : t('daterange.select_period', { ns: 'energyxplorer' });

  return (
    <LocalizationProvider
      dateAdapter={AdapterLuxon}
      adapterLocale={locale}
      localeText={
        locale === 'da'
          ? daDK.components.MuiLocalizationProvider.defaultProps.localeText
          : undefined
      }
    >
      <Tooltip
        title={t('daterange.select_period', { ns: 'energyxplorer' })}
        enterDelay={500}
        placement="top"
      >
        <span>
          <Button
            startIcon={<CalendarTodayRoundedIcon />}
            variant="contained"
            onClick={handleOpen}
            disabled={disabled}
            disableRipple
            // sx={{ ':hover': { background: 'none' } }}
          >
            {triggerLabel}
          </Button>
        </span>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ border: '1px solid', borderColor: 'divider' }}>
          <StaticDateRangePicker
            displayStaticWrapperAs="mobile"
            disableFuture
            calendars={2}
            value={tempRange}
            onChange={(range) => {
              if (!range || !range[0] || !range[1]) return;
              setTempRange(range);
            }}
            // Using day-level views for clarity; switch to hours if you need time-of-day
            views={['year', 'month', 'day']}
            dayOfWeekFormatter={(d) => d.toLocaleString({ weekday: 'short' })}
            slotProps={{
              shortcuts: { items: shortcuts, style: { borderRadius: 8 } },
              actionBar: { actions: [] },
            }}
          />

          <Box
            display="flex"
            justifyContent="flex-end"
            gap={1}
            sx={{ p: 2, backgroundColor: 'background.paper' }}
          >
            <Button onClick={handleClose}>
              {t('dialogs.daterange.close')}
            </Button>

            <Button
              variant="contained"
              onClick={handleConfirmAndGenerate}
              disabled={!tempRange?.[0] || !tempRange?.[1] || !canGenerate}
            >
              {generateLabel}
            </Button>
          </Box>
        </Box>
      </Popover>
    </LocalizationProvider>
  );
}
